import { lazy, Suspense, useEffect, useRef, useState, useCallback } from "react";
import { X, MapPin, Navigation } from "lucide-react";
import { useMobileOverlayData } from "@/contexts/MobileOverlayContext";
import { buildListingPath } from "@/lib/listings";
import { useNavigate } from "react-router-dom";

const LocationMap = lazy(() => import("@/components/LocationMap"));

interface MiniListing {
  id: string;
  name: string;
  cover_photo?: string;
  photos?: string[];
  lat?: number | null;
  lng?: number | null;
  distance_km?: number;
  city?: string;
  neighborhood?: string;
  state?: string;
  is_verified?: boolean;
  featured?: boolean;
  featured_until?: string;
}

function distanceLabelOf(l: MiniListing): string | null {
  const km = l.distance_km;
  if (km == null) return null;
  return km < 1 ? `${Math.round(km * 1000)} m` : `${km.toFixed(1)} km`;
}

function locationTextOf(l: MiniListing): string {
  const parts = [
    l.neighborhood ? `${l.neighborhood}, ` : "",
    l.city || "",
    l.state ? ` · ${l.state}` : "",
  ].filter(Boolean);
  return parts.join("").trim() || "Localização indisponível";
}

export default function MobileMapStrip({ open, onClose }: { open: boolean; onClose: () => void }): React.JSX.Element {
  const { mapProps, filterProps } = useMobileOverlayData();
  const navigate = useNavigate();
  const stripRef = useRef<HTMLDivElement>(null);
  const rowRef = useRef<HTMLDivElement>(null);
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Clear user location and search radius, keep category/subcategory/state/city
  const handleClearLocation = useCallback(() => {
    if (!filterProps) return;
    filterProps.onLocationChange({
      state: filterProps.location.state,
      city: filterProps.location.city,
      neighborhood: "",
    });
    filterProps.setMaxKm(null);
    filterProps.clearUserLocation?.();
  }, [filterProps]);

  const listings: MiniListing[] = (mapProps?.listings ?? []) as unknown as MiniListing[];
  const userLocation = mapProps?.userLocation ?? null;
  const maxKm = mapProps?.maxKm ?? null;
  const filterState = mapProps?.filterState ?? "";
  const filterCity = mapProps?.filterCity ?? "";

  // Sem dados, o strip fica vazio.
  const focusable = listings.filter((l) => l.lat != null && l.lng != null);

  // Mantém o foco válido quando a lista muda.
  useEffect(() => {
    if (!focusable.length) {
      setFocusedId(null);
      return;
    }
    setFocusedId((prev) => {
      if (prev && focusable.some((l) => l.id === prev)) return prev;
      return focusable[0].id;
    });
  }, [focusable]);

  // Inicializa focusedId no mount para evitar salto para userLocation
  useEffect(() => {
    if (focusable.length && !focusedId) {
      setFocusedId(focusable[0].id);
    }
  }, [focusable]);

  // Observa os mini-cards na faixa horizontal para atualizar o mapa em tempo
  // real enquanto o usuário desliza.
  useEffect(() => {
    if (!open || !rowRef.current) return;
    observerRef.current?.disconnect();
    observerRef.current = new IntersectionObserver(
      (entries) => {
        let best: IntersectionObserverEntry | null = null;
        for (const e of entries) {
          if (!e.isIntersecting) continue;
          if (!best || e.intersectionRatio > best.intersectionRatio) best = e;
        }
        if (best) {
          const id = (best.target as HTMLElement).dataset.id;
          if (id) setFocusedId(id);
        }
      },
      { root: rowRef.current, threshold: [0.5] },
    );
    const cards = rowRef.current.querySelectorAll<HTMLElement>("[data-mini-card]");
    cards.forEach((el) => observerRef.current?.observe(el));
    return () => {
      observerRef.current?.disconnect();
      observerRef.current = null;
    };
  }, [open, focusable]);

  const focused = focusable.find((l) => l.id === focusedId) ?? focusable[0] ?? null;

  // Auto-preenche estado/cidade do anúncio em foco quando filtros estão vazios
  const autoFilledRef = useRef(false);
  const prevFocusedRef = useRef<string | null>(null);

  // Reset auto-fill flag when map closes
  useEffect(() => {
    if (!open) {
      autoFilledRef.current = false;
      prevFocusedRef.current = null;
    }
  }, [open]);

  useEffect(() => {
    if (!open || !focused) return;
    if (filterState || filterCity) return; // Já preenchido manualmente
    if (!focused.state || !filterProps?.onLocationChange) return;

    // Evita loop: só preenche uma vez por sessão de abertura do mapa
    if (autoFilledRef.current) return;

    // Só chama se o foco mudou para um anúncio diferente
    if (prevFocusedRef.current === focused.id) return;
    prevFocusedRef.current = focused.id;

    autoFilledRef.current = true;
    filterProps.onLocationChange({
      state: focused.state,
      city: focused.city || "",
    });
  }, [open, focused, filterState, filterCity, filterProps]);

  const isFeaturedActive = (l: MiniListing): boolean => 
    !!l.featured && !!l.featured_until && new Date(l.featured_until) > new Date();

  const markers = focusable.map((l) => ({
    id: l.id,
    lat: l.lat as number,
    lng: l.lng as number,
    name: l.name,
    photo: l.cover_photo || (l.photos && l.photos[0]) || null,
    href: buildListingPath(l) || `/acompanhante/${l.id}`,
    distanceLabel: distanceLabelOf(l),
    neighborhood: l.neighborhood || null,
    isVerified: l.is_verified,
    featured: isFeaturedActive(l),
  }));

  const focusPoint =
    focused && focused.lat != null && focused.lng != null
      ? { lat: focused.lat, lng: focused.lng }
      : (focusable[0] ? { lat: focusable[0].lat as number, lng: focusable[0].lng as number } : userLocation);

  const handleNavigateToProfile = (id: string) => {
    const listing = focusable.find((l) => l.id === id);
    if (listing) {
      const path = buildListingPath(listing) || `/acompanhante/${id}`;
      navigate(path);
    }
  };

  if (!mapProps) {
    return (
      <div
        data-testid="mobile-map-strip"
        className="md:hidden fixed inset-0 z-[60] hidden"
        aria-hidden
      />
    );
  }

  const DISTANCE_OPTIONS = [
    { label: "Todos", value: null },
    { label: "5 km", value: 5 },
    { label: "10 km", value: 10 },
    { label: "20 km", value: 20 },
    { label: "50 km", value: 50 },
  ] as const;

  return (
    <div
      data-testid="mobile-map-strip"
      className={`md:hidden fixed inset-0 z-[60] transition-opacity duration-200 ${
        open ? "opacity-100" : "opacity-0 pointer-events-none"
      }`}
      aria-hidden={!open}
    >
      <div
        ref={stripRef}
        className="fixed inset-0 z-[60] bg-cream-50 flex flex-col pb-[calc(65px+env(safe-area-inset-bottom))]"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2 border-b border-cream-200 bg-white">
          <div className="flex items-center gap-2">
            <MapPin className="w-5 h-5 text-heat-600" />
            <span className="font-display font-bold text-base text-ink-900">
              Mapa
            </span>
            {focusable.length > 0 && (
              <span className="text-[11px] font-bold text-ink-400">
                {focusable.length} anúncio{focusable.length === 1 ? "" : "s"}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {(userLocation || maxKm) && (
              <div className="flex items-center gap-2">
                <select
                  value={maxKm ?? ""}
                  onChange={(e) => {
                    const val = e.target.value === "" ? null : Number(e.target.value);
                    filterProps?.setMaxKm(val);
                  }}
                  className="text-[11px] font-bold bg-white border border-cream-200 rounded-full px-3 py-1.5 appearance-none bg-no-repeat bg-right pr-8"
                  style={{
                    backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E\")",
                    backgroundPosition: "right 0.5rem center",
                  }}
                  aria-label="Raio de distância"
                >
                  {DISTANCE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value ?? ""}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={handleClearLocation}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-bold text-heat-600 bg-heat-50 border border-heat-200 hover:bg-heat-100 transition"
                  aria-label="Esquecer localização e raio"
                >
                  <MapPin className="w-3.5 h-3.5" />
                  Esquecer
                </button>
              </div>
            )}
<button
            type="button"
            onClick={onClose}
            className="min-w-[44px] min-h-[44px] w-11 h-11 inline-flex items-center justify-center rounded-full hover:bg-cream-100 text-ink-600 transition"
            aria-label="Fechar mapa"
          >
            <X className="w-5 h-5" />
          </button>
          </div>
        </div>

        {/* Faixa horizontal de cards - um por vez, clica vai para perfil completo */}
        <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
          <div className="px-4 pt-3 pb-1 flex items-center justify-between">
            <span className="text-[10px] font-bold uppercase tracking-wider text-heat-700">
              Deslize para ver no mapa
            </span>
            {focused && (
              <span className="text-[11px] font-bold text-ink-600 truncate max-w-[60%]">
                {focused.name}
              </span>
            )}
          </div>
          <div
            ref={rowRef}
            className="flex-1 min-h-[120px] overflow-x-auto overflow-y-hidden snap-x snap-mandatory px-4 pb-4 flex gap-3 items-stretch"
          >
            {focusable.length === 0 ? (
              <div className="m-auto text-center text-ink-400 text-sm w-full snap-center shrink-0">
                Nenhum anúncio com localização próxima.
              </div>
            ) : (
              focusable.map((l) => {
                const active = l.id === focusedId;
                return (
                  <button
                    key={l.id}
                    type="button"
                    data-id={l.id}
                    data-mini-card
                    onClick={() => handleNavigateToProfile(l.id)}
                    className={[
                      "snap-center shrink-0 w-[85%] max-w-[280px] text-left rounded-2xl overflow-hidden border bg-white shadow-sm transition-all",
                      active
                        ? "border-heat-500 shadow-heat-glow scale-[1.02]"
                        : "border-cream-200 opacity-80",
                    ].join(" ")}
                  >
                    <div className="relative h-32 bg-cream-100">
                      <img
                        src={l.cover_photo || (l.photos && l.photos[0]) || "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=800"}
                        alt={l.name}
                        loading="lazy"
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute top-2 left-2 flex flex-col gap-1">
                        {l.is_verified && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-blue-600 text-white text-[9px] font-bold shadow-sm">
                            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>
                            Verificado
                          </span>
                        )}
                        {isFeaturedActive(l) && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-500 text-white text-[9px] font-bold shadow-sm">
                            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
                            Destaque
                          </span>
                        )}
                      </div>
                      {distanceLabelOf(l) && (
                        <span className="absolute top-2 right-2 inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-full bg-ink-900/90 text-white dark:bg-heat-500/90 dark:text-cream-50">
                          <Navigation className="w-3 h-3" /> {distanceLabelOf(l)}
                        </span>
                      )}
                    </div>
                    <div className="p-2.5">
                      <p className="text-sm font-display font-bold text-ink-900 line-clamp-1">
                        {l.name}
                      </p>
                      <p className="text-[11px] text-ink-500 line-clamp-1">
                        {locationTextOf(l)}
                      </p>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* Mapa no rodapé - menor */}
        <div className="relative h-[34vh] shrink-0 bg-cream-100 min-h-[180px] max-h-[320px] border-t border-cream-200">
          <Suspense
            fallback={<div className="w-full h-full bg-cream-100 animate-pulse" />}
          >
            <LocationMap
              value={userLocation}
              markers={markers}
              focus={focusPoint}
              readOnly
              valueKind={userLocation ? "user" : "profile"}
              maxKm={maxKm}
              focusedMarkerId={focusedId}
              hideControls
              allowZoom
            />
          </Suspense>
        </div>
      </div>
    </div>
  );
}